import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'fad_cart_v1';

const initial = { items: [] };

// Read persisted cart synchronously so the reducer starts already-hydrated.
// This avoids a mount-tick race where the persist effect wrote empty state
// before an async HYDRATE dispatch committed (which dropped the cart on a
// hard refresh / direct navigation to /checkout).
const initFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && Array.isArray(parsed.items)) return parsed;
  } catch (e) {
    // ignore
  }
  return initial;
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'HYDRATE':
      return action.payload || state;
    case 'ADD': {
      const { lineId, productId, slug, name, price, image, variantSku, variantOption, quantity, specialOrder } = action.payload;
      const existing = state.items.find((i) => i.lineId === lineId);
      if (existing) {
        return {
          ...state,
          items: state.items.map((i) =>
            i.lineId === lineId ? { ...i, quantity: i.quantity + quantity } : i
          ),
        };
      }
      return {
        ...state,
        items: [
          ...state.items,
          { lineId, productId, slug, name, price, image, variantSku, variantOption, quantity, specialOrder: !!specialOrder },
        ],
      };
    }
    case 'UPDATE_QTY': {
      const { lineId, quantity } = action.payload;
      if (quantity <= 0) {
        return { ...state, items: state.items.filter((i) => i.lineId !== lineId) };
      }
      return {
        ...state,
        items: state.items.map((i) => (i.lineId === lineId ? { ...i, quantity } : i)),
      };
    }
    case 'REMOVE':
      return { ...state, items: state.items.filter((i) => i.lineId !== action.payload.lineId) };
    case 'CLEAR':
      return initial;
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initial, initFromStorage);
  const [isOpen, setOpen] = React.useState(false);

  // Persist to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // ignore
    }
  }, [state]);

  const totals = useMemo(() => {
    const itemCount = state.items.reduce((n, i) => n + i.quantity, 0);
    const subtotal = state.items.reduce((n, i) => n + i.price * i.quantity, 0);
    return { itemCount, subtotal };
  }, [state.items]);

  const value = useMemo(
    () => ({
      items: state.items,
      ...totals,
      isOpen,
      openCart: () => setOpen(true),
      closeCart: () => setOpen(false),
      toggleCart: () => setOpen((o) => !o),
      addItem: (item, { open = true } = {}) => {
        dispatch({ type: 'ADD', payload: item });
        if (open) setOpen(true);
      },
      updateQty: (lineId, quantity) => dispatch({ type: 'UPDATE_QTY', payload: { lineId, quantity } }),
      removeItem: (lineId) => dispatch({ type: 'REMOVE', payload: { lineId } }),
      clear: () => dispatch({ type: 'CLEAR' }),
    }),
    [state.items, totals, isOpen]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
